/**
 * Only loads an image when it's about to be visible in the viewport.
 *
 * The next time we need it, it remembers and then behaves like a normal <img> tag.
 */

import useEventCallback from '@pkgs/shared-client/hooks/useEventCallback';
import IntersectionObserverAdmin from 'intersection-observer-admin';
import React, { useEffect, useRef, useState } from 'react';
import { useMountedState } from 'react-use';

const SUPPORTS_INTERSECTION_OBSERVER =
	typeof window === 'object' && 'IntersectionObserver' in window;
const EMPTY_GIF =
	'data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==';
const OBSERVER_OPTIONS = { rootMargin: '200px', threshold: 0.01 };

const cache = new Map();
const globalObserver = new IntersectionObserverAdmin();

const _SVLazyImage = ({
	src: imageSrc,
	alt,
	className,
	style,
	decoding,
}: {
	src: HTMLImageElement['src'];
	alt?: HTMLImageElement['alt'];
	className?: string;
	style?: React.HTMLProps<HTMLImageElement>['style'];
	decoding?: HTMLImageElement['decoding'];
}) => {
	const [inView, setInView] = useState(!SUPPORTS_INTERSECTION_OBSERVER);
	const imageRef = useRef<HTMLImageElement>(null);
	const isMounted = useMountedState();

	const isOnCache = cache.has(imageSrc);
	const shouldLoadImage = !SUPPORTS_INTERSECTION_OBSERVER || inView || isOnCache;

	if (inView && !isOnCache) {
		cache.set(imageSrc, true);
	}

	const removeObserver = useEventCallback(() => {
		if (imageRef.current) {
			globalObserver.unobserve(imageRef.current, OBSERVER_OPTIONS);
		}
	});

	const handleInView = useEventCallback(() => {
		removeObserver();

		setTimeout(() => {
			if (!isMounted) {
				return;
			}

			setInView(true);
		}, 0);
	});

	useEffect(() => {
		if (!SUPPORTS_INTERSECTION_OBSERVER || shouldLoadImage || !imageRef.current) {
			return;
		}

		globalObserver.addEnterCallback(imageRef.current, handleInView);
		globalObserver.observe(imageRef.current, OBSERVER_OPTIONS);

		return removeObserver;
	}, [shouldLoadImage, imageRef.current, removeObserver, handleInView]);

	return (
		<img
			ref={imageRef}
			src={shouldLoadImage ? String(imageSrc) : EMPTY_GIF}
			alt={alt}
			className={className}
			style={style}
			decoding={decoding}
		/>
	);
};

const SVLazyImage = React.memo(_SVLazyImage);

export default SVLazyImage;
